<template>
	<Form ref="formRef" @submit="handleNextStep" @invalid-submit="onInvalidSubmit">
	<DoorsaleLayout
		:title="$t('checkout.choose_tickets')"
		:current-step="1"
		:event-id="eventId"
		:on-back="handleBack"
		:on-forward="handleForward"
	>
		<template #header>
			<CheckoutTicketHeader v-if="checkoutStore.event" />
			<CheckoutCodeInput
				v-if="checkoutStore.event.has_vouchercodes == 1"
				class="md:hidden"
				:hasDiscountCode="true"
				:hasGiftCard="false"
				@on-change="handleCodeChange"
			/>
		</template>

		<template #header-actions>
			<button
				v-if="checkoutStore.event.has_vouchercodes == 1"
				type="button"
				@click="toggleVouchercodeInput"
				class="hidden md:flex mr-auto border border-surface-grey px-4 py-1 cursor-pointer rounded-full text-sm font-semibold hover:bg-surface-grey-light transition-colors"
			>
				{{ checkoutStore.event.voucher_code_add_description }}
			</button>
		</template>

		<div class="space-y-4">
			<BaseSlideTransition>
				<CheckoutTicketsNotice
					v-if="checkoutStore.noticeTickets.length > 0 && checkoutStore.vouchercode?.code?.length > 0"
				>
					<span v-if="checkoutStore.noticeTickets.length > 1">
						{{ $t('checkout.extra_tickets_shown', { count: checkoutStore.noticeTickets.length }) }}
					</span>
					<span v-else>
						{{ $t('checkout.extra_ticket_shown') }}
					</span>
					<span class="ml-1" v-if="checkoutStore.vouchercode?.code?.length > 0">
						{{ checkoutStore.vouchercode.code }}
					</span>
				</CheckoutTicketsNotice>
			</BaseSlideTransition>

			<CheckoutTicket
				v-for="ticket in checkoutStore.displayTickets"
				:key="ticket.id"
				:ticket="ticket"
				:event="checkoutStore.event"
				:eventID="eventId"
			/>
		</div>

		<template #aside>
			<CheckoutCodeInput
				v-if="checkoutStore.event.has_vouchercodes == 1"
				ref="codeInput"
				class="md:block hidden"
				:hasDiscountCode="true"
				:hasGiftCard="false"
				@on-change="handleCodeChange"
			/>
			<div class="md:sticky md:top-24">
				<CheckoutSummary :primary-label="$t('doorsale.continue')" :can-proceed="hasNonUpsaleTicketForCurrentEvent" />
			</div>
		</template>
	</DoorsaleLayout>
	</Form>
</template>

<script setup lang="ts">
	import { useHead } from '@unhead/vue';
	import { Form, type InvalidSubmissionHandler } from 'vee-validate';
	import { computed, onMounted, ref, watch } from 'vue';
	import { useI18n } from 'vue-i18n';
	import { useRoute, useRouter } from 'vue-router';

	import CheckoutCodeInput from '@/components/checkout/CheckoutCodeInput.vue';
	import CheckoutSummary from '@/components/checkout/CheckoutSummary.vue';
	import CheckoutTicket from '@/components/checkout/CheckoutTicket.vue';
	import CheckoutTicketHeader from '@/components/checkout/CheckoutTicketHeader.vue';
	import CheckoutTicketsNotice from '@/components/checkout/CheckoutTicketsNotice.vue';
	import DoorsaleLayout from '@/components/doorsale/DoorsaleLayout.vue';
	import BaseSlideTransition from '@/components/ui/BaseSlideTransition.vue';
	import { useCartStore } from '@/stores/cart';
	import { useCheckoutStore } from '@/stores/checkoutStore';
	import { useToastStore } from '@/stores/toastStore';
	import { useDoorsaleNavigation } from '@/utils/doorsaleNavigation';
	import { useWebsocketStore } from '@/websocket/websocket';

	const { t } = useI18n();
	const route = useRoute();
	const router = useRouter();
	const websocketStore = useWebsocketStore();
	const checkoutStore = useCheckoutStore();
	const cart = useCartStore();
	const toastStore = useToastStore();

	const codeInput = ref<InstanceType<typeof CheckoutCodeInput> | null>(null);
	const formRef = ref<InstanceType<typeof Form> | null>(null);

	function toggleVouchercodeInput() {
		codeInput.value?.toggleVouchercodeInput();
	}

	const eventId = computed<number | string>(() => {
		const p = route.params.eventId as string;
		return /^[0-9a-f]{96}$/.test(p) ? p : Number(p);
	});
	const { nextStep, previousStep } = useDoorsaleNavigation(eventId);

	function handleBack() {
		previousStep('doorsale-tickets');
	}

	function handleForward() {
		if (!hasNonUpsaleTicketForCurrentEvent.value) return;
		(formRef.value?.$el as HTMLFormElement | undefined)?.requestSubmit();
	}

	const hasNonUpsaleTicketForCurrentEvent = computed(() =>
		checkoutStore.displayTickets.some(t => (cart.currentCount[t.id] ?? 0) > 0)
	);

	const subscribeEventSent = ref(false);

	function initEvent(id: number | string) {
		checkoutStore.clearCheckoutData();
		checkoutStore.setCheckoutID(id);
		if (!subscribeEventSent.value) {
			websocketStore.send('subscribeevent', { id, is_doorsale: true }, undefined);
			subscribeEventSent.value = true;
		}
	}

	function handleNextStep() {
		nextStep('doorsale-tickets');
	}

	const onInvalidSubmit: InvalidSubmissionHandler = submission => {
		const firstErrorFieldName = Object.keys(submission.errors)[0];
		const el = document.querySelector(`[name="${firstErrorFieldName}"]`);
		if (el) {
			(el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	};

	function handleCodeChange() {
		// triggers reactivity on vouchercode-dependent UI
	}

	watch(() => cart.differentCount, newValue => {
		if (newValue) {
			const message = cart.differentCountMessage.message;
			const count = cart.differentCountMessage.count;
			const ticketsText = (count == 1 ? t('common.ticket') : t('common.ticket_plural')).toLocaleLowerCase();
			if (message && message == 'initial') {
				toastStore.showToast(t('checkout.reservation_mismatch', { tickets: ticketsText }), 15000);
			} else if (message && count) {
				toastStore.showToast(t('checkout.reservation_mismatch_' + message, { count, tickets: ticketsText }), 15000);
			}
			cart.differentCount = false;
		}
	});

	watch(() => eventId.value, newId => {
		subscribeEventSent.value = false;
		if (newId) {
			initEvent(newId);
		}
	});

	onMounted(() => {
		if (!eventId.value) {
			router.push({ name: 'doorsale-events' });
			return;
		}
		websocketStore.open();
		initEvent(eventId.value);
	});

	useHead({
		title: t('checkout.choose_tickets'),
		meta: [{ property: 'og:title', content: t('checkout.choose_tickets') }],
	});
</script>

